// Subscription Management Utilities for Backoffice
import { doc, getDoc, setDoc, collection, getDocs, serverTimestamp, query, where, orderBy, limit } from 'firebase/firestore';
import { backofficeDb, backofficeAuth } from '../backofficeFirebaseClient';
import {
  VALID_PLAN_IDS,
  VALID_STATUSES,
  VALID_BILLING,
  SERVER_MANAGED_SUBSCRIPTION_FIELDS,
  SUBSCRIPTION_PATCH_BODY_KEYS,
  SUBSCRIPTION_FULL_MERGE_BODY_KEYS,
  assertNoExtraKeys
} from '../constants/subscriptionContract';

const stripServerManagedFields = (payload) => {
  const cleaned = { ...payload };
  SERVER_MANAGED_SUBSCRIPTION_FIELDS.forEach((key) => {
    delete cleaned[key];
  });
  return cleaned;
};

const resolveSubscriptionPlan = (company) =>
  company?.subscription_details?.subscription?.basePlan?.planId ||
  company?.subscription_details?.subscription?.planId ||
  company?.subscription_details?.subscription?.plan ||
  company?.subscription?.basePlan?.planId ||
  company?.subscription?.planId ||
  company?.subscription?.plan ||
  'starter';

const normalizeSubscription = (companyId, subscription = {}) => {
  const basePlan = subscription.basePlan || {};
  const planId = basePlan.planId || subscription.planId || subscription.plan || 'starter';
  const billing = basePlan.billing || subscription.billing || 'monthly';
  const status = subscription.status || basePlan.status || 'active';
  const limits = subscription.totalEntitlements || basePlan.limits || subscription.limits || {};
  const version = Number(subscription.version || 0);

  return {
    companyId,
    plan: planId,
    planId,
    planName: basePlan.planName || subscription.planName || null,
    status,
    billing,
    limits: {
      firebase_operations: Number(limits.firebase_operations || 0),
      storage_mb: Number(limits.storage_mb || 0),
      sales_documents: Number(limits.sales_documents || 0),
      parties: Number(limits.parties || 0)
    },
    basePlan,
    totalEntitlements: subscription.totalEntitlements || null,
    topups: Array.isArray(subscription.topups) ? subscription.topups : [],
    currentMonthUsage: subscription.currentMonthUsage || {},
    updated_by: subscription.updated_by || null,
    updated_at: subscription.updated_at || null,
    version
  };
};

const validateCanonicalWritePayload = (payload) => {
  const errors = [];
  const required = ['planId', 'planName', 'price', 'billing', 'status', 'limits'];
  required.forEach((key) => {
    if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
      errors.push(`${key} is required`);
    }
  });
  if (payload.planId && !VALID_PLAN_IDS.includes(payload.planId)) {
    errors.push(`Unsupported planId: ${payload.planId}`);
  }
  if (payload.status && !VALID_STATUSES.includes(payload.status)) {
    errors.push(`Unsupported status: ${payload.status}`);
  }
  if (payload.billing && !VALID_BILLING.includes(payload.billing)) {
    errors.push(`Unsupported billing: ${payload.billing}`);
  }
  const limits = payload.limits || {};
  ['firebase_operations', 'storage_mb', 'sales_documents', 'parties'].forEach((k) => {
    const value = limits[k];
    if (value !== undefined && value !== null && (Number.isNaN(Number(value)) || Number(value) < 0)) {
      errors.push(`limits.${k} must be a non-negative number`);
    }
  });
  if (payload.expectedVersion !== undefined && (!Number.isInteger(payload.expectedVersion) || payload.expectedVersion < 0)) {
    errors.push('expectedVersion must be a non-negative integer');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

const toCanonicalWritePayload = (subscriptionData, options = {}) => {
  const now = new Date().toISOString();
  return {
    planId: subscriptionData.planId || subscriptionData.basePlan?.planId || subscriptionData.plan || 'starter',
    planName: subscriptionData.planName || subscriptionData.basePlan?.planName || 'Starter Plan',
    price: Number(subscriptionData.price ?? subscriptionData.basePlan?.price ?? 0),
    currency: subscriptionData.currency || subscriptionData.basePlan?.currency || '₹',
    billing: subscriptionData.billing || subscriptionData.basePlan?.billing || 'monthly',
    status: subscriptionData.status || subscriptionData.basePlan?.status || 'active',
    limits: {
      firebase_operations: Number(
        subscriptionData.limits?.firebase_operations ??
          subscriptionData.basePlan?.limits?.firebase_operations ??
          0
      ),
      storage_mb: Number(
        subscriptionData.limits?.storage_mb ??
          subscriptionData.basePlan?.limits?.storage_mb ??
          0
      ),
      sales_documents: Number(
        subscriptionData.limits?.sales_documents ??
          subscriptionData.basePlan?.limits?.sales_documents ??
          0
      ),
      parties: Number(
        subscriptionData.limits?.parties ??
          subscriptionData.basePlan?.limits?.parties ??
          0
      )
    },
    customLimits: subscriptionData.customLimits || null,
    hasCustomEntitlement: !!subscriptionData.hasCustomEntitlement,
    expectedVersion: options.expectedVersion,
    clientTimestamp: now
  };
};

// Get all companies via API (secure method)
export const getAllCompanies = async () => {
  try {
    // Import backoffice auth for token
    const { backofficeAuth } = await import('../backofficeFirebaseClient');
    
    const token = await backofficeAuth.currentUser?.getIdToken?.();
    if (!token) {
      throw new Error('Admin authentication required');
    }

    // Use API endpoint instead of direct Firebase access
    const response = await fetch('https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/companies?limit=1000', {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    const companies = (data?.companies || []).map(company => ({
      id: company.id,
      name: company.company_info?.firmName || company.name || 'Unknown Company',
      plan: resolveSubscriptionPlan(company),
      status: company.company_info?.status || 'active',
      created_at: company.company_info?.created_at || company.created_at
    }));

    return companies;
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

// Get subscription data for a specific company via API
export const getCompanySubscription = async (companyId) => {
  try {
    // Import backoffice auth for token
    const { backofficeAuth } = await import('../backofficeFirebaseClient');
    
    const token = await backofficeAuth.currentUser?.getIdToken?.();
    if (!token) {
      throw new Error('Admin authentication required');
    }

    // Use API endpoint to get company details including subscription
    const response = await fetch(`https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/companies/${companyId}`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    const company = data?.company;
    
    if (!company) {
      throw new Error('Company not found');
    }

    const subscription = company.subscription_details?.subscription || company.subscription || {};
    return normalizeSubscription(companyId, subscription);
  } catch (error) {
    console.error('Error fetching subscription:', error);
    throw error;
  }
};

export const readSubscription = async (companyId) => {
  return getCompanySubscription(companyId);
};

// Update subscription for a specific company via API
export const updateCompanySubscription = async (companyId, subscriptionData, adminInfo) => {
  return writeSubscription(companyId, subscriptionData, adminInfo);
};

export const writeSubscription = async (companyId, subscriptionData, adminInfo, options = {}) => {
  try {
    // Import backoffice auth for token
    const { backofficeAuth } = await import('../backofficeFirebaseClient');
    
    const token = await backofficeAuth.currentUser?.getIdToken?.();
    if (!token) {
      throw new Error('Admin authentication required');
    }

    const canonicalPayload = toCanonicalWritePayload(subscriptionData, options);
    const validation = validateCanonicalWritePayload(canonicalPayload);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const updateData = stripServerManagedFields({
      ...canonicalPayload,
      ...(options.idempotency_key ? { idempotency_key: options.idempotency_key } : {})
    });

    const cleaned = Object.fromEntries(Object.entries(updateData).filter(([, v]) => v !== undefined));

    const extraCheck = assertNoExtraKeys(cleaned, SUBSCRIPTION_PATCH_BODY_KEYS);
    if (!extraCheck.ok) {
      throw new Error(extraCheck.errors.join(', '));
    }

    // Use API endpoint to update subscription
    const response = await fetch(`https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/admin/companies/${companyId}/subscription`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cleaned)
    });

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status} - ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${errorData.message || errorData.error || 'Unknown error'}`;
      } catch (e) {
        const errorText = await response.text();
        errorMessage += ` - ${errorText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // Log the subscription change
    await logSubscriptionChange(companyId, cleaned, adminInfo);
    
    return { success: true, result };
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

/**
 * Full-document merge write (e.g. plan upgrade Variant B). Server enforces version + idempotency.
 */
export const writeSubscriptionFullMerge = async (companyId, subscriptionDocument, adminInfo, options = {}) => {
  const { backofficeAuth } = await import('../backofficeFirebaseClient');
  const token = await backofficeAuth.currentUser?.getIdToken?.();
  if (!token) {
    throw new Error('Admin authentication required');
  }

  const { expectedVersion, idempotency_key } = options;
  if (expectedVersion === undefined || expectedVersion === null) {
    throw new Error('expectedVersion is required for full merge');
  }
  if (!idempotency_key || typeof idempotency_key !== 'string') {
    throw new Error('idempotency_key is required for full merge');
  }

  const docPayload = { ...subscriptionDocument };
  delete docPayload.version;

  const body = {
    write_mode: 'full_merge',
    document: docPayload,
    expectedVersion: Number(expectedVersion),
    idempotency_key
  };

  const extraCheck = assertNoExtraKeys(body, SUBSCRIPTION_FULL_MERGE_BODY_KEYS);
  if (!extraCheck.ok) {
    throw new Error(extraCheck.errors.join(', '));
  }

  const response = await fetch(`https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/admin/companies/${companyId}/subscription`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} - ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage += ` - ${errorData.message || errorData.error || 'Unknown error'}`;
    } catch (e) {
      const errorText = await response.text();
      errorMessage += ` - ${errorText}`;
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  await logSubscriptionChange(companyId, { write_mode: 'full_merge', planId: docPayload.basePlan?.planId }, adminInfo);
  return { success: true, result };
};

// Log subscription changes for audit trail
export const logSubscriptionChange = async (companyId, subscriptionData, adminInfo) => {
  try {
    // Ensure authentication before accessing app database
    const { ensureAppServiceSignIn } = await import('../appFirebaseClient');
    await ensureAppServiceSignIn();
    
    // Use the main app database for subscription logs
    const { getAppFirestore } = await import('../appFirebaseClient');
    const appDb = getAppFirestore();
    
    const logsRef = collection(appDb, 'subscription_logs');
    
    await setDoc(doc(logsRef), {
      company_id: companyId,
      action: 'subscription_updated',
      changes: subscriptionData,
      admin_info: {
        uid: adminInfo.uid,
        email: adminInfo.email,
        name: adminInfo.displayName || adminInfo.email
      },
      timestamp: serverTimestamp(),
      ip_address: null // Could be added if needed
    });
    
    console.log('✅ Subscription change logged successfully');
  } catch (error) {
    console.error('❌ Error logging subscription change:', error);
    // Don't throw error for logging failures - subscription update already succeeded
  }
};

// Get subscription logs for a company
export const getSubscriptionLogs = async (companyId, limitCount = 50) => {
  try {
    // Use the main app database for subscription logs
    const { getAppFirestore } = await import('../appFirebaseClient');
    const appDb = getAppFirestore();
    
    const logsRef = collection(appDb, 'subscription_logs');
    
    // Query logs for specific company
    const q = query(
      logsRef,
      where('company_id', '==', companyId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const logs = [];
    
    snapshot.forEach(doc => {
      logs.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return logs;
  } catch (error) {
    console.error('Error fetching subscription logs:', error);
    throw error;
  }
};

// Validate subscription data
export const validateSubscriptionData = (subscriptionData) => {
  const errors = [];
  
  if (!subscriptionData.plan) {
    errors.push('Plan is required');
  }
  
  if (!subscriptionData.features || typeof subscriptionData.features !== 'object') {
    errors.push('Features configuration is required');
  }
  
  // Validate usage limits are numbers or -1 for unlimited
  Object.entries(subscriptionData.features || {}).forEach(([category, categoryFeatures]) => {
    Object.entries(categoryFeatures).forEach(([featureKey, featureData]) => {
      if (featureData.hasUsage && featureData.usage_limit !== undefined) {
        if (typeof featureData.usage_limit !== 'number' || (featureData.usage_limit < -1)) {
          errors.push(`Invalid usage limit for ${category}.${featureKey}: must be a number >= -1`);
        }
      }
    });
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Format subscription data for display
export const formatSubscriptionForDisplay = (subscriptionData) => {
  if (!subscriptionData) return null;
  
  const enabledFeatures = [];
  const disabledFeatures = [];
  let totalUsageLimit = 0;
  let currentUsage = 0;
  
  Object.entries(subscriptionData.features || {}).forEach(([category, categoryFeatures]) => {
    Object.entries(categoryFeatures).forEach(([featureKey, featureData]) => {
      const featureName = `${category}.${featureKey}`;
      
      if (featureData.enabled) {
        enabledFeatures.push(featureName);
        
        if (featureData.usage_limit !== undefined) {
          if (featureData.usage_limit === -1) {
            totalUsageLimit = -1; // Unlimited
          } else if (totalUsageLimit !== -1) {
            totalUsageLimit += featureData.usage_limit;
          }
          
          if (featureData.current_usage !== undefined) {
            currentUsage += featureData.current_usage;
          }
        }
      } else {
        disabledFeatures.push(featureName);
      }
    });
  });
  
  return {
    plan: subscriptionData.plan,
    enabledFeatures,
    disabledFeatures,
    totalUsageLimit,
    currentUsage,
    usagePercentage: totalUsageLimit === -1 ? 0 : 
      totalUsageLimit === 0 ? 0 : Math.round((currentUsage / totalUsageLimit) * 100),
    lastUpdated: subscriptionData.updated_at,
    updatedBy: subscriptionData.updated_by
  };
};

// Get subscription statistics
export const getSubscriptionStats = async () => {
  try {
    const companies = await getAllCompanies();
    
    const stats = {
      total_companies: companies.length,
      plan_distribution: {
        basic: 0,
        pro: 0,
        max: 0,
        custom: 0
      },
      active_companies: 0,
      inactive_companies: 0
    };
    
    companies.forEach(company => {
      // Count plan distribution
      if (stats.plan_distribution[company.plan]) {
        stats.plan_distribution[company.plan]++;
      } else {
        stats.plan_distribution.custom++;
      }
      
      // Count active/inactive
      if (company.status === 'active') {
        stats.active_companies++;
      } else {
        stats.inactive_companies++;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error fetching subscription stats:', error);
    throw error;
  }
};

// Plan Management Functions

/**
 * Save a subscription plan to Firebase
 * @param {string} planKey - Plan key (e.g., 'starter', 'basic', 'pro', 'enterprise')
 * @param {Object} planData - Plan data including name, pricing, features, etc.
 * @param {Object} adminInfo - Admin who created/updated the plan
 * @returns {Promise<void>}
 */
export const saveSubscriptionPlan = async (planKey, planData, adminInfo) => {
  try {
    if (!VALID_PLAN_IDS.includes(planKey)) {
      throw new Error(
        `Plan ID must be one of: ${VALID_PLAN_IDS.join(', ')}. Got "${planKey}".`
      );
    }

    const planRef = doc(backofficeDb, 'subscription_plans', planKey);
    const catalogVersion = Date.now();

    const planToSave = {
      ...planData,
      plan_key: planKey,
      limits: {
        firebase_operations: Number(planData.limits?.firebase_operations ?? 0),
        storage_mb: Number(planData.limits?.storage_mb ?? 0),
        sales_documents: Number(planData.limits?.sales_documents ?? 0),
        parties: Number(planData.limits?.parties ?? 0)
      },
      updated_at: serverTimestamp(),
      updated_by: adminInfo,
      version: catalogVersion
    };

    await setDoc(planRef, planToSave, { merge: true });

    const { syncCatalogPlanToApp } = await import('./planCatalogSync');
    await syncCatalogPlanToApp(planKey, planToSave);

    console.log(`Plan ${planKey} saved to backoffice catalog and synced to app plans/${planKey}`);
  } catch (error) {
    console.error('Error saving subscription plan:', error);
    throw error;
  }
};

/**
 * Normalize Firebase plan data to match USAGE_PLANS format
 * Handles both Firebase structure (pricing.monthly_amount, features) and direct structure (price, limits)
 * @param {Object} firebasePlan - Raw plan data from Firebase
 * @param {string} planId - Plan ID (document ID)
 * @returns {Object} - Normalized plan in USAGE_PLANS format
 */
const normalizePlanToUsageFormat = (firebasePlan, planId) => {
  // If plan already has the expected USAGE_PLANS structure, return as-is
  if (firebasePlan.price && firebasePlan.limits && firebasePlan.limits.firebase_operations !== undefined) {
    return {
      id: planId,
      name: firebasePlan.name || firebasePlan.planName || `${planId} Plan`,
      price: firebasePlan.price,
      currency: firebasePlan.currency || "₹",
      billing: firebasePlan.billing || "monthly",
      limits: {
        firebase_operations: firebasePlan.limits.firebase_operations || 0,
        storage_mb: firebasePlan.limits.storage_mb || 0,
        sales_documents: firebasePlan.limits.sales_documents || 0,
        parties: firebasePlan.limits.parties || 0
      },
      description: firebasePlan.description || ""
    };
  }

  // Convert from old Firebase structure (pricing.monthly_amount, features) to USAGE_PLANS format
  // Extract price
  const price = firebasePlan.price || firebasePlan.pricing?.monthly_amount || 0;
  
  // Extract limits - handle both new structure (limits) and old structure (features)
  let limits = { firebase_operations: 0, storage_mb: 0, sales_documents: 0, parties: 0 };

  if (firebasePlan.limits) {
    limits = {
      firebase_operations: firebasePlan.limits.firebase_operations || 0,
      storage_mb: firebasePlan.limits.storage_mb || 0,
      sales_documents: firebasePlan.limits.sales_documents || 0,
      parties: firebasePlan.limits.parties || 0
    };
  } else if (firebasePlan.features) {
    limits = {
      firebase_operations: firebasePlan.features.firebase_operations || 0,
      storage_mb: firebasePlan.features.storage_mb || 0,
      sales_documents: firebasePlan.features.sales_documents || 0,
      parties: firebasePlan.features.parties || 0
    };
  }

  return {
    id: planId,
    name: firebasePlan.name || `${planId} Plan`,
    price: price,
    currency: firebasePlan.currency || "₹",
    billing: firebasePlan.billing || "monthly",
    limits: limits,
    description: firebasePlan.description || ""
  };
};

/**
 * Load all subscription plans from Firebase
 * Returns plans in USAGE_PLANS format (directly compatible with SubscriptionManager)
 * @returns {Promise<Object>} - Object containing all plans in USAGE_PLANS format
 */
export const loadSubscriptionPlans = async () => {
  try {
    const plansRef = collection(backofficeDb, 'subscription_plans');
    const snapshot = await getDocs(plansRef);
    
    const plans = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Normalize each plan to USAGE_PLANS format
      plans[doc.id] = normalizePlanToUsageFormat(data, doc.id);
    });
    
    console.log('✅ Loaded subscription plans from Firebase:', Object.keys(plans));
    return plans;
  } catch (error) {
    console.error('❌ Error loading subscription plans:', error);
    throw error;
  }
};

/**
 * Load a specific subscription plan from Firebase
 * Returns plan in USAGE_PLANS format (directly compatible with SubscriptionManager)
 * @param {string} planKey - Plan key to load
 * @returns {Promise<Object|null>} - Normalized plan data in USAGE_PLANS format or null if not found
 */
export const loadSubscriptionPlan = async (planKey) => {
  try {
    const planRef = doc(backofficeDb, 'subscription_plans', planKey);
    const snapshot = await getDoc(planRef);
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      // Normalize to USAGE_PLANS format
      return normalizePlanToUsageFormat(data, snapshot.id);
    }
    
    return null;
  } catch (error) {
    console.error('Error loading subscription plan:', error);
    throw error;
  }
};

/**
 * Initialize default plans in Firebase (run once)
 * @param {Object} defaultPlans - Default plans object
 * @param {Object} adminInfo - Admin info
 * @returns {Promise<void>}
 */
export const initializeDefaultPlans = async (defaultPlans, adminInfo) => {
  try {
    for (const [planKey, planData] of Object.entries(defaultPlans)) {
      const planRef = doc(backofficeDb, 'subscription_plans', planKey);
      
      const planToSave = {
        ...planData,
        plan_key: planKey,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: adminInfo,
        updated_by: adminInfo,
        version: 1,
        is_default: true
      };
      
      await setDoc(planRef, planToSave);
      console.log(`Initialized plan: ${planKey}`);
    }
    
    console.log('All default plans initialized successfully');
  } catch (error) {
    console.error('Error initializing default plans:', error);
    throw error;
  }
};

/**
 * Get plan usage statistics
 * @returns {Promise<Object>} - Plan usage stats
 */
export const getPlanUsageStats = async () => {
  try {
    const companies = await getAllCompanies();
    const stats = {
      total_companies: companies.length,
      plan_distribution: {},
      trial_companies: 0
    };
    
    companies.forEach(company => {
      const plan = company.plan || 'basic';
      stats.plan_distribution[plan] = (stats.plan_distribution[plan] || 0) + 1;
      
      if (company.subscription?.trial_start_date && !company.subscription?.trial_ended) {
        stats.trial_companies++;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error fetching plan usage stats:', error);
    throw error;
  }
};

// Activity Logging Functions

/**
 * Log subscription activity
 * @param {string} companyId - Company ID
 * @param {string} action - Action performed (e.g., 'plan_changed', 'trial_started', 'trial_ended')
 * @param {Object} details - Action details
 * @param {Object} adminInfo - Admin who performed the action
 * @returns {Promise<void>}
 */
export const logSubscriptionActivity = async (companyId, action, details, adminInfo) => {
  try {
    const activityRef = doc(collection(backofficeDb, 'subscription_activities'));
    
    const activityData = {
      company_id: companyId,
      action: action,
      details: details,
      admin_info: adminInfo,
      timestamp: serverTimestamp(),
      created_at: new Date().toISOString()
    };
    
    await setDoc(activityRef, activityData);
    console.log(`Activity logged: ${action} for company ${companyId}`);
  } catch (error) {
    console.error('Error logging subscription activity:', error);
    // Don't throw error to avoid breaking main functionality
  }
};

/**
 * Get subscription activities for a company
 * @param {string} companyId - Company ID
 * @param {number} limit - Number of activities to fetch (default: 50)
 * @returns {Promise<Array>} - Array of activities
 */
export const getCompanySubscriptionActivities = async (companyId, limitCount = 50) => {
  try {
    const activitiesRef = collection(backofficeDb, 'subscription_activities');
    const q = query(
      activitiesRef,
      where('company_id', '==', companyId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const activities = [];
    
    snapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return activities;
  } catch (error) {
    console.error('Error fetching subscription activities:', error);
    throw error;
  }
};

/**
 * Get all subscription activities (admin view)
 * @param {number} limit - Number of activities to fetch (default: 100)
 * @returns {Promise<Array>} - Array of activities
 */
export const getAllSubscriptionActivities = async (limitCount = 100) => {
  try {
    const { backofficeApiFetch } = await import('./backofficeApi');
    const { activities } = await backofficeApiFetch(
      `/v1/subscription-activities?limit=${encodeURIComponent(limitCount)}`
    );
    return Array.isArray(activities) ? activities : [];
  } catch (apiError) {
    console.warn('Activity API failed, trying Firestore:', apiError.message);
    const activitiesRef = collection(backofficeDb, 'subscription_activities');
    const q = query(activitiesRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    const activities = [];
    snapshot.forEach((d) => activities.push({ id: d.id, ...d.data() }));
    return activities;
  }
};

// ============================================
// NEW FUNCTIONS FOR TOP-UP & VARIANT B SYSTEM
// ============================================

/**
 * Upgrade plan using Variant B logic (Full charge + immediate time extension)
 * @param {string} companyId - Company ID
 * @param {string} newPlanId - New plan ID
 * @param {Object} newPlanData - New plan data (from USAGE_PLANS)
 * @param {Object} adminInfo - Admin info who initiated upgrade
 * @returns {Promise<Object>} - Upgrade result
 */
export const upgradePlanVariantB = async (companyId, newPlanId, newPlanData, adminInfo) => {
  try {
    console.log(`🔄 Upgrading company ${companyId} to plan ${newPlanId} (Variant B)`);
    
    // Import necessary utilities
    const { calculateRemainingValue, calculateNextResetDate, calculateTotalEntitlements } = await import('./subscriptionCalculations');
    
    // Ensure authentication before accessing app database
    const { ensureAppServiceSignIn } = await import('../appFirebaseClient');
    await ensureAppServiceSignIn();
    
    // Get current subscription from main app database
    const { getAppFirestore } = await import('../appFirebaseClient');
    const appDb = getAppFirestore();
    
    const subscriptionRef = doc(appDb, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      throw new Error('Current subscription not found');
    }
    
    const oldSubscription = subscriptionSnap.data();
    const now = new Date();
    
    // Calculate remaining value from old plan
    const remaining = calculateRemainingValue(oldSubscription);
    
    console.log(`📊 Remaining value:`, remaining);
    
    // Create upgrade bonus top-up (remaining operations)
    const upgradeBonus = {
      topupId: `UPGRADE_BONUS_${Date.now()}`,
      source: "plan_upgrade",
      purchaseDate: now.toISOString(),
      price: 0, // Free bonus
      name: "🎁 Upgrade Bonus Pack",
      description: `Remaining quota from ${oldSubscription.basePlan?.planName || oldSubscription.planName || 'previous plan'}`,
      
      limits: {
        firebase_operations: remaining.operations,
        storage_mb: remaining.storage,
        sales_documents: remaining.documents,
        parties: remaining.parties
      },
      
      remaining: {
        firebase_operations: remaining.operations,
        storage_mb: remaining.storage,
        sales_documents: remaining.documents,
        parties: remaining.parties
      },
      
      status: "active",
      expiryDate: null, // Till use
      
      metadata: {
        fromPlan: oldSubscription.basePlan?.planId || oldSubscription.planId,
        toPlan: newPlanId,
        upgradeDate: now.toISOString()
      }
    };
    
    // Calculate new validity with bonus days (Variant B)
    const newValidityDays = 30 + remaining.daysRemaining;
    const newResetDate = new Date(now);
    newResetDate.setDate(newResetDate.getDate() + newValidityDays);
    
    // Keep existing active topups
    const existingTopups = oldSubscription.topups?.filter(t => t.status === 'active') || [];
    
    // Create new subscription structure
    const newSubscription = {
      basePlan: {
        planId: newPlanId,
        planName: newPlanData.name,
        price: newPlanData.price,
        currency: newPlanData.currency || '₹',
        billing: newPlanData.billing || 'monthly',
        
        startDate: now.toISOString(),
        nextResetDate: newResetDate.toISOString(),
        nextBillingDate: newResetDate.toISOString(),
        
        purchaseDate: now.toISOString(),
        paidAmount: newPlanData.price,
        validityDays: newValidityDays,
        
        limits: newPlanData.limits,
        
        bonusDays: {
          total: remaining.daysRemaining,
          source: `plan_upgrade_from_${oldSubscription.basePlan?.planId || oldSubscription.planId}`,
          appliedOn: now.toISOString(),
          description: `Time credit from ${oldSubscription.basePlan?.planName || 'previous plan'}`
        },
        
        autoRenew: oldSubscription.basePlan?.autoRenew !== undefined ? oldSubscription.basePlan.autoRenew : true,
        paymentMethod: oldSubscription.basePlan?.paymentMethod || 'manual',
        status: "active"
      },
      
      topups: [
        ...existingTopups,
        upgradeBonus
      ],
      
      totalEntitlements: calculateTotalEntitlements(
        { limits: newPlanData.limits },
        [...existingTopups, upgradeBonus]
      ),
      
      currentMonthUsage: {
        period: `${now.toISOString().split('T')[0]} to ${newResetDate.toISOString().split('T')[0]}`,
        startDate: now.toISOString(),
        resetDate: newResetDate.toISOString(),
        
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0,
        
        usageBreakdown: {
          fromBasePlan: {
            firebase_operations: 0,
            storage_mb: 0,
            sales_documents: 0,
            parties: 0
          },
          fromTopups: {}
        },
        
        lastUpdated: now.toISOString()
      },
      
      upgradeHistory: [
        ...(oldSubscription.upgradeHistory || []),
        {
          upgradeId: `UPG_${Date.now()}`,
          date: now.toISOString(),
          
          fromPlan: {
            planId: oldSubscription.basePlan?.planId || oldSubscription.planId,
            planName: oldSubscription.basePlan?.planName || oldSubscription.planName,
            price: oldSubscription.basePlan?.price || oldSubscription.price,
            remainingValue: remaining
          },
          
          toPlan: {
            planId: newPlanId,
            planName: newPlanData.name,
            price: newPlanData.price
          },
          
          benefits: {
            operationsBonus: remaining.operations,
            storageBonus: remaining.storage,
            documentsBonus: remaining.documents,
            partiesBonus: remaining.parties,
            timeBonusDays: remaining.daysRemaining,
            totalValue: remaining.monetaryValue
          },
          
          billing: {
            chargeAmount: newPlanData.price,
            paymentMethod: "pending",
            paymentStatus: "pending"
          },
          
          performedBy: {
            source: "admin",
            adminId: adminInfo.uid,
            adminEmail: adminInfo.email
          }
        }
      ],
      
      scheduledDowngrade: null,
      
      status: "active",
      created_at: oldSubscription.created_at || now.toISOString(),
      updated_at: now.toISOString(),
      updated_by: {
        admin_id: adminInfo.uid,
        admin_email: adminInfo.email,
        admin_name: adminInfo.displayName || adminInfo.email
      }
    };
    
    // Save new subscription via API endpoint (more secure and reliable)
    console.log(`📤 Saving upgrade via API endpoint...`);
    const idempotency_key = `variantB:${companyId}:${newPlanId}:${Date.now()}`;
    await writeSubscriptionFullMerge(
      companyId,
      newSubscription,
      adminInfo,
      {
        expectedVersion: Number(oldSubscription.version || 0),
        idempotency_key
      }
    );
    
    // Log activity (with authentication)
    try {
      await logSubscriptionActivity(companyId, 'plan_upgraded', {
        from: oldSubscription.basePlan?.planId || oldSubscription.planId,
        to: newPlanId,
        benefits: newSubscription.upgradeHistory[newSubscription.upgradeHistory.length - 1].benefits,
        chargeAmount: newPlanData.price
      }, adminInfo);
    } catch (logError) {
      console.warn('⚠️ Could not log upgrade activity:', logError.message);
      // Don't fail the upgrade if logging fails
    }
    
    console.log(`✅ Upgrade successful for company ${companyId}`);
    
    return {
      success: true,
      newSubscription,
      chargeAmount: newPlanData.price,
      benefits: {
        newValidityDays: newValidityDays,
        validUntil: newResetDate.toISOString(),
        operationsBonus: remaining.operations,
        timeBonusDays: remaining.daysRemaining,
        totalOperations: newPlanData.limits.firebase_operations + remaining.operations
      }
    };
    
  } catch (error) {
    console.error('❌ Error upgrading plan:', error);
    throw error;
  }
};

/**
 * Purchase top-up for a company
 * @param {string} companyId - Company ID
 * @param {string} topupId - Top-up ID from catalog
 * @param {Object} topupData - Top-up data from catalog
 * @param {Object} paymentInfo - Payment information
 * @returns {Promise<Object>} - Purchase result
 */
export const purchaseTopup = async (companyId, topupId, topupData, paymentInfo) => {
  try {
    console.log(`🔄 Purchasing top-up ${topupId} for company ${companyId}`);
    
    // Import necessary utilities
    const { calculateTotalEntitlements } = await import('./subscriptionCalculations');
    const { validateTopupPurchase } = await import('./subscriptionValidation');
    
    // Get current subscription
    const { getAppFirestore } = await import('../appFirebaseClient');
    const appDb = getAppFirestore();
    
    const subscriptionRef = doc(appDb, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      throw new Error('Subscription not found');
    }
    
    const subscription = subscriptionSnap.data();
    const now = new Date();
    
    // Validate purchase
    const validation = validateTopupPurchase(topupData, { id: companyId, status: 'active' });
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }
    
    // Create new topup
    const newTopup = {
      topupId: `TOPUP_${Date.now()}`,
      catalogId: topupId,
      source: "purchase",
      purchaseDate: now.toISOString(),
      price: topupData.price,
      
      name: topupData.name,
      description: topupData.description,
      
      limits: topupData.limits,
      
      remaining: { ...topupData.limits },
      
      status: "active",
      expiryDate: null, // Till use
      
      payment: {
        ...paymentInfo,
        paidAt: now.toISOString()
      }
    };
    
    // Add to topups array
    const updatedTopups = [...(subscription.topups || []), newTopup];
    
    // Recalculate total entitlements
    const totalEntitlements = calculateTotalEntitlements(
      subscription.basePlan,
      updatedTopups
    );
    
    // Update subscription
    const updatedSubscription = {
      ...subscription,
      topups: updatedTopups,
      totalEntitlements,
      updated_at: now.toISOString()
    };
    
    await setDoc(subscriptionRef, updatedSubscription);
    
    // Record payment
    await recordPayment(companyId, {
      ...paymentInfo,
      type: 'topup',
      topupId: newTopup.topupId,
      topupName: topupData.name,
      amount: topupData.price
    });
    
    console.log(`✅ Top-up purchased successfully for company ${companyId}`);
    
    return {
      success: true,
      topup: newTopup,
      updatedSubscription
    };
    
  } catch (error) {
    console.error('Error purchasing top-up:', error);
    throw error;
  }
};

/**
 * Record payment (manual or automated)
 * @param {string} companyId - Company ID
 * @param {Object} paymentData - Payment data
 * @returns {Promise<Object>} - Record result
 */
export const recordPayment = async (companyId, paymentData) => {
  try {
    const { getAppFirestore } = await import('../appFirebaseClient');
    const appDb = getAppFirestore();

    const idBase =
      paymentData.idempotency_key ||
      `manual_${companyId}_${paymentData.reference || 'noref'}_${paymentData.date || ''}_${paymentData.amount || ''}`;
    const paymentId = String(idBase)
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .slice(0, 450);

    const paymentRef = doc(appDb, 'payment_history', paymentId);

    await setDoc(
      paymentRef,
      {
        company_id: companyId,
        ...paymentData,
        idempotency_key: paymentData.idempotency_key || paymentId,
        recorded_at: serverTimestamp()
      },
      { merge: true }
    );

    console.log(`✅ Payment recorded for company ${companyId}`);

    return { success: true, paymentId: paymentRef.id };
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};
